import { z } from 'zod';
import { httpsCallable, type Functions } from 'firebase/functions';
import { createBackendGatewayError, isBackendGatewayError } from '../../utils/backendGatewayError';
import {
  USER_ACCOUNT_ROLES,
  type PrivacyPolicyAcceptanceReadModel,
  type UserAccount,
  type UserAccountRole,
} from './userAccount';
import { userAccountListQueryCapabilities } from './userAccountQueryCapabilities';
import type { QueryOperandConstraint } from '../queryCapabilities/queryCapabilities';
import { normalizeUserAccountFilters, normalizeUserAccountSortField } from './userAccountQuery';
import type {
  BackofficeExportFormat,
  ExportArtifactResult,
} from '../exportArtifacts/exportArtifacts';
import type {
  CreateUserAccountArgs,
  CreateUserAccountResult,
  DeleteUserAccountArgs,
  DisableUserAccountArgs,
  EnableUserAccountArgs,
  InitiateUserCredentialResetArgs,
  InitiateUserCredentialSetupArgs,
  ListUserAccountsArgs,
  PaginatedUserAccounts,
  UpdateUserAccountArgs,
  UserCredentialResetResult,
  UserCredentialSetupResult,
  UserAccountFilterItem,
  UserAccountsBackendGateway,
} from './userAccountsGateway';

const USER_ACCOUNTS_BACKEND_OPERATION = 'user-account';

const CALLABLE_NAMES = {
  listUserAccounts: 'listUsers',
  exportUserAccounts: 'exportUserAccounts',
  createUserAccount: 'createUser',
  initiateUserCredentialSetup: 'initiateUserCredentialSetup',
  initiateUserCredentialReset: 'initiateUserCredentialReset',
  updateUserAccount: 'updateUser',
  disableUserAccount: 'disableUser',
  enableUserAccount: 'enableUser',
  deleteUserAccount: 'deleteUser',
} as const;

const RawRoleArraySchema = z.array(z.enum(USER_ACCOUNT_ROLES));

const RawPrivacyPolicyAcceptanceSchema = z
  .object({
    version: z.string(),
    locale: z.string(),
    displayUrl: z.string(),
    mediaType: z.enum(['text/html', 'application/pdf']),
    contentHash: z.string(),
    privacyPolicyContentDigest: z.string().optional(),
    privacyPolicyPublicationArtifactDigest: z.string().optional(),
    acceptedAt: z.string(),
  })
  .strict();

const RawUserAccountSchema = z
  .object({
    uid: z.string(),
    email: z.string().nullable(),
    displayName: z.string().nullable(),
    disabled: z.boolean(),
    emailVerified: z.boolean(),
    createdAt: z.string(),
    lastSignedInAt: z.string().nullable(),
    roles: RawRoleArraySchema,
    privacyPolicyAcceptance: RawPrivacyPolicyAcceptanceSchema.nullish(),
  })
  .strict();

const ListUserAccountsResponseSchema = z.object({
  users: z.array(RawUserAccountSchema),
  totalCount: z.number().optional(),
});

const ExportArtifactDownloadSchema = z
  .object({
    kind: z.literal('download'),
    downloadUrl: z.string().url(),
    fileName: z.string(),
    contentType: z.string(),
  })
  .strict();

const ExportArtifactEmptySchema = z
  .object({
    kind: z.literal('empty'),
    message: z.string(),
  })
  .strict();

const ExportUserAccountsResponseSchema = z.union([
  ExportArtifactDownloadSchema,
  ExportArtifactEmptySchema,
]);
const CreateUserAccountResponseSchema = z
  .object({
    success: z.literal(true),
    uid: z.string(),
  })
  .strict();
const InitiateUserCredentialSetupResponseSchema = z
  .object({
    success: z.literal(true),
    uid: z.string(),
    email: z.string().email(),
    credentialSetupLink: z.string().url(),
  })
  .strict();
const InitiateUserCredentialResetResponseSchema = z
  .object({
    success: z.literal(true),
    uid: z.string(),
    email: z.string().email(),
    credentialResetLink: z.string().url(),
  })
  .strict();

type ExportUserAccountsArgs = Omit<ListUserAccountsArgs, 'page' | 'pageSize'> & {
  format: BackofficeExportFormat;
};

const toUserAccount = (payload: z.infer<typeof RawUserAccountSchema>): UserAccount => {
  return {
    uid: payload.uid,
    email: payload.email,
    displayName: payload.displayName,
    disabled: payload.disabled,
    emailVerified: payload.emailVerified,
    createdAt: payload.createdAt,
    lastSignedInAt: payload.lastSignedInAt,
    roles: payload.roles,
    privacyPolicyAcceptance: toPrivacyPolicyAcceptanceReadModel(payload.privacyPolicyAcceptance),
  };
};

const toPrivacyPolicyAcceptanceReadModel = (
  payload: z.infer<typeof RawPrivacyPolicyAcceptanceSchema> | null | undefined
): PrivacyPolicyAcceptanceReadModel | null => {
  if (!payload) {
    return null;
  }

  return {
    version: payload.version,
    locale: payload.locale,
    displayUrl: payload.displayUrl,
    mediaType: payload.mediaType,
    contentHash: payload.contentHash,
    acceptedAt: payload.acceptedAt,
  };
};

type CreateUserAccountCallableArgs = Omit<CreateUserAccountArgs, 'roles'> & {
  roles: UserAccountRole[];
};

type UpdateUserAccountCallableArgs = Omit<UpdateUserAccountArgs, 'roles'> & {
  roles?: UserAccountRole[];
};

const cleanFilters = (filters: UserAccountFilterItem[]) =>
  normalizeUserAccountFilters(filters)
    .map(toValidatedFilter)
    .filter((item): item is UserAccountFilterItem => item !== null);

const createInvalidQueryError = (message: string) =>
  createBackendGatewayError(USER_ACCOUNTS_BACKEND_OPERATION, 'invalid-query', new Error(message));

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const getCallableErrorDetails = (error: unknown): Record<string, unknown> | null => {
  if (!isRecord(error)) {
    return null;
  }

  if (isRecord(error.details)) {
    return error.details;
  }

  if (isRecord(error.customData) && isRecord(error.customData.details)) {
    return error.customData.details;
  }

  return null;
};

const getStructuredExportErrorReason = (
  error: unknown
): 'invalid-query' | 'export-result-too-large' | 'export-planner-unavailable' | null => {
  const details = getCallableErrorDetails(error);
  const reason = typeof details?.reason === 'string' ? details.reason : null;

  if (reason === 'user-account/export-result-too-large') {
    return 'export-result-too-large';
  }

  if (reason === 'user-account/export-planner-unavailable') {
    return 'export-planner-unavailable';
  }

  if (reason?.includes('invalid-query')) {
    return 'invalid-query';
  }

  if (
    isRecord(error) &&
    typeof error.code === 'string' &&
    error.code.includes('invalid-argument')
  ) {
    return 'invalid-query';
  }

  return null;
};

const getFieldCapability = (field: string) =>
  userAccountListQueryCapabilities.fields.find((capability) => capability.field === field);

const getOperatorCapability = (field: string, operator: string) =>
  getFieldCapability(field)?.operators.find((capability) => capability.operator === operator);

const getUnsupportedFilterCombination = (field: string, operator: string) =>
  userAccountListQueryCapabilities.unsupportedCombinations.find(
    (combination) => combination.field === field && combination.operator === operator
  );

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const isDateOnlyString = (value: unknown): value is string => {
  if (typeof value !== 'string') return false;

  const match = DATE_ONLY_PATTERN.exec(value);
  if (!match) return false;

  const [, yearValue, monthValue, dayValue] = match;
  const year = Number(yearValue);
  const month = Number(monthValue);
  const day = Number(dayValue);
  const date = new Date(Date.UTC(year, month - 1, day));

  return (
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
  );
};

const isValidFilterOperandValue = (
  value: UserAccountFilterItem['value'],
  operand: QueryOperandConstraint
): boolean => {
  if (operand.kind === 'none') {
    return value === undefined || value === null;
  }

  if (operand.kind !== 'single') {
    return false;
  }

  switch (operand.valueKind) {
    case 'text':
      return typeof value === 'string';
    case 'boolean':
      return typeof value === 'boolean';
    case 'date':
      return isDateOnlyString(value);
    case 'user-account-role':
      return typeof value === 'string' && (USER_ACCOUNT_ROLES as readonly string[]).includes(value);
    default:
      return false;
  }
};

const toValidatedFilter = (filter: UserAccountFilterItem): UserAccountFilterItem | null => {
  const unsupportedCombination = getUnsupportedFilterCombination(filter.field, filter.op);
  if (unsupportedCombination) {
    throw createInvalidQueryError(
      `Unsupported user account filter combination: ${filter.field}.${filter.op}`
    );
  }

  const fieldCapability = getFieldCapability(filter.field);
  if (!fieldCapability) {
    throw createInvalidQueryError(`Unsupported user account filter field: ${filter.field}`);
  }

  const operatorCapability = getOperatorCapability(filter.field, filter.op);
  if (!operatorCapability) {
    throw createInvalidQueryError(
      `Unsupported user account filter operator: ${filter.field}.${filter.op}`
    );
  }

  if (!isValidFilterOperandValue(filter.value, operatorCapability.operand)) {
    throw createInvalidQueryError(
      `Unsupported user account filter value: ${filter.field}.${filter.op}`
    );
  }

  if (
    operatorCapability.operand.minimumLength !== undefined &&
    typeof filter.value === 'string' &&
    filter.value.trim().length < operatorCapability.operand.minimumLength
  ) {
    throw createInvalidQueryError(
      `Unsupported user account filter value length: ${filter.field}.${filter.op}`
    );
  }

  return {
    ...filter,
  };
};

const assertFilterCount = (filters: UserAccountFilterItem[]) => {
  if (filters.length > userAccountListQueryCapabilities.compound.maximumFilters) {
    throw createInvalidQueryError(
      `User account list supports at most ${userAccountListQueryCapabilities.compound.maximumFilters} filters`
    );
  }
};

const toBackendSortField = (sortField: string | null | undefined): string | undefined => {
  const normalizedSortField = normalizeUserAccountSortField(sortField);
  if (normalizedSortField === undefined) return undefined;
  if (!userAccountListQueryCapabilities.sortableFields.includes(normalizedSortField)) {
    throw createInvalidQueryError(`Unsupported user account sort field: ${normalizedSortField}`);
  }

  return normalizedSortField;
};

const toListUserAccountsPayload = (params: ListUserAccountsArgs): ListUserAccountsArgs => {
  const filters = cleanFilters(params.filters);
  assertFilterCount(filters);

  const payload: ListUserAccountsArgs = {
    filters,
    page: params.page + 1,
    pageSize: params.pageSize,
  };

  const sortField = toBackendSortField(params.sortField);
  if (sortField !== undefined) {
    payload.sortField = sortField;
  }
  if (params.sortDir !== undefined && params.sortDir !== null) {
    payload.sortDir = params.sortDir;
  }

  return payload;
};

const toExportUserAccountsPayload = (params: ExportUserAccountsArgs): ExportUserAccountsArgs => {
  const filters = cleanFilters(params.filters);
  assertFilterCount(filters);

  const payload: ExportUserAccountsArgs = {
    filters,
    format: params.format,
  };

  const sortField = toBackendSortField(params.sortField);
  if (sortField !== undefined) {
    payload.sortField = sortField;
  }
  if (params.sortDir !== undefined && params.sortDir !== null) {
    payload.sortDir = params.sortDir;
  }

  return payload;
};

const toCreateUserAccountCallableArgs = (
  args: CreateUserAccountArgs
): CreateUserAccountCallableArgs => {
  const callableArgs: CreateUserAccountCallableArgs = {
    email: args.email,
    roles: args.roles ?? [],
  };
  if (args.displayName !== undefined) callableArgs.displayName = args.displayName;

  return callableArgs;
};

const toUpdateUserAccountCallableArgs = (
  args: UpdateUserAccountArgs
): UpdateUserAccountCallableArgs => {
  const callableArgs: UpdateUserAccountCallableArgs = {
    uid: args.uid,
  };
  if (args.email !== undefined) callableArgs.email = args.email;
  if (args.displayName !== undefined) callableArgs.displayName = args.displayName;
  if (args.roles !== undefined) callableArgs.roles = args.roles;

  return callableArgs;
};

export const createFirebaseUserAccountsBackendGateway = (
  functions: Functions
): UserAccountsBackendGateway => ({
  async listUserAccounts(
    params: Omit<ListUserAccountsArgs, 'page' | 'pageSize'> & {
      page: number;
      pageSize: number;
    }
  ): Promise<PaginatedUserAccounts> {
    try {
      const listUserAccountsCallable = httpsCallable<
        ListUserAccountsArgs,
        z.infer<typeof ListUserAccountsResponseSchema>
      >(functions, CALLABLE_NAMES.listUserAccounts);

      const response = await listUserAccountsCallable(toListUserAccountsPayload(params));

      const parsedResponse = ListUserAccountsResponseSchema.parse(response.data);
      const items = parsedResponse.users.map(toUserAccount);

      return {
        items,
        totalCount: parsedResponse.totalCount ?? items.length,
      };
    } catch (error) {
      if (error instanceof z.ZodError) {
        throw createBackendGatewayError(
          USER_ACCOUNTS_BACKEND_OPERATION,
          'response-validation',
          error
        );
      }
      if (isBackendGatewayError(error)) {
        throw error;
      }

      throw createBackendGatewayError(USER_ACCOUNTS_BACKEND_OPERATION, 'list-failed', error);
    }
  },

  async exportUserAccounts(params: ExportUserAccountsArgs): Promise<ExportArtifactResult> {
    try {
      const exportUserAccountsCallable = httpsCallable<
        ExportUserAccountsArgs,
        z.infer<typeof ExportUserAccountsResponseSchema>
      >(functions, CALLABLE_NAMES.exportUserAccounts);

      const response = await exportUserAccountsCallable(toExportUserAccountsPayload(params));
      return ExportUserAccountsResponseSchema.parse(response.data);
    } catch (error) {
      if (error instanceof z.ZodError) {
        throw createBackendGatewayError(
          USER_ACCOUNTS_BACKEND_OPERATION,
          'response-validation',
          error
        );
      }
      if (isBackendGatewayError(error)) {
        throw error;
      }

      const structuredReason = getStructuredExportErrorReason(error);
      if (structuredReason) {
        throw createBackendGatewayError(USER_ACCOUNTS_BACKEND_OPERATION, structuredReason, error);
      }

      throw createBackendGatewayError(USER_ACCOUNTS_BACKEND_OPERATION, 'export-failed', error);
    }
  },

  async createUserAccount(args: CreateUserAccountArgs): Promise<CreateUserAccountResult> {
    try {
      const createUserAccountCallable = httpsCallable<
        CreateUserAccountCallableArgs,
        z.infer<typeof CreateUserAccountResponseSchema>
      >(functions, CALLABLE_NAMES.createUserAccount);
      const response = await createUserAccountCallable(toCreateUserAccountCallableArgs(args));
      const parsedResponse = CreateUserAccountResponseSchema.parse(response.data);

      return {
        uid: parsedResponse.uid,
      };
    } catch (error) {
      if (error instanceof z.ZodError) {
        throw createBackendGatewayError(
          USER_ACCOUNTS_BACKEND_OPERATION,
          'response-validation',
          error
        );
      }
      throw createBackendGatewayError(USER_ACCOUNTS_BACKEND_OPERATION, 'create-failed', error);
    }
  },

  async initiateUserCredentialSetup(
    args: InitiateUserCredentialSetupArgs
  ): Promise<UserCredentialSetupResult> {
    try {
      const initiateUserCredentialSetupCallable = httpsCallable<
        InitiateUserCredentialSetupArgs,
        z.infer<typeof InitiateUserCredentialSetupResponseSchema>
      >(functions, CALLABLE_NAMES.initiateUserCredentialSetup);
      const response = await initiateUserCredentialSetupCallable(args);
      const parsedResponse = InitiateUserCredentialSetupResponseSchema.parse(response.data);

      return {
        uid: parsedResponse.uid,
        email: parsedResponse.email,
        credentialSetupLink: parsedResponse.credentialSetupLink,
      };
    } catch (error) {
      if (error instanceof z.ZodError) {
        throw createBackendGatewayError(
          USER_ACCOUNTS_BACKEND_OPERATION,
          'response-validation',
          error
        );
      }
      throw createBackendGatewayError(
        USER_ACCOUNTS_BACKEND_OPERATION,
        'credential-setup-failed',
        error
      );
    }
  },

  async initiateUserCredentialReset(
    args: InitiateUserCredentialResetArgs
  ): Promise<UserCredentialResetResult> {
    try {
      const initiateUserCredentialResetCallable = httpsCallable<
        InitiateUserCredentialResetArgs,
        z.infer<typeof InitiateUserCredentialResetResponseSchema>
      >(functions, CALLABLE_NAMES.initiateUserCredentialReset);
      const response = await initiateUserCredentialResetCallable(args);
      const parsedResponse = InitiateUserCredentialResetResponseSchema.parse(response.data);

      return {
        uid: parsedResponse.uid,
        email: parsedResponse.email,
        credentialResetLink: parsedResponse.credentialResetLink,
      };
    } catch (error) {
      if (error instanceof z.ZodError) {
        throw createBackendGatewayError(
          USER_ACCOUNTS_BACKEND_OPERATION,
          'response-validation',
          error
        );
      }
      throw createBackendGatewayError(
        USER_ACCOUNTS_BACKEND_OPERATION,
        'credential-reset-failed',
        error
      );
    }
  },

  async updateUserAccount(args: UpdateUserAccountArgs): Promise<void> {
    try {
      const updateUserAccountCallable = httpsCallable<
        UpdateUserAccountCallableArgs,
        { success: boolean }
      >(functions, CALLABLE_NAMES.updateUserAccount);
      await updateUserAccountCallable(toUpdateUserAccountCallableArgs(args));
    } catch (error) {
      throw createBackendGatewayError(USER_ACCOUNTS_BACKEND_OPERATION, 'update-failed', error);
    }
  },

  async disableUserAccount(args: DisableUserAccountArgs): Promise<void> {
    try {
      const disableUserAccountCallable = httpsCallable<
        DisableUserAccountArgs,
        { success: boolean }
      >(functions, CALLABLE_NAMES.disableUserAccount);
      await disableUserAccountCallable(args);
    } catch (error) {
      throw createBackendGatewayError(USER_ACCOUNTS_BACKEND_OPERATION, 'disable-failed', error);
    }
  },

  async enableUserAccount(args: EnableUserAccountArgs): Promise<void> {
    try {
      const enableUserAccountCallable = httpsCallable<EnableUserAccountArgs, { success: boolean }>(
        functions,
        CALLABLE_NAMES.enableUserAccount
      );
      await enableUserAccountCallable(args);
    } catch (error) {
      throw createBackendGatewayError(USER_ACCOUNTS_BACKEND_OPERATION, 'enable-failed', error);
    }
  },

  async deleteUserAccount(args: DeleteUserAccountArgs): Promise<void> {
    try {
      const deleteUserAccountCallable = httpsCallable<DeleteUserAccountArgs, { success: boolean }>(
        functions,
        CALLABLE_NAMES.deleteUserAccount
      );
      await deleteUserAccountCallable(args);
    } catch (error) {
      throw createBackendGatewayError(USER_ACCOUNTS_BACKEND_OPERATION, 'delete-failed', error);
    }
  },
});
